/**
 * useUnblockShipment - Hook para desbloquear shipments individuales
 * 
 * Permite desbloquear un shipment específico eliminando su registro de bloqueo.
 * Realiza DELETE a /invoices/shipments/{id}/block donde id es el ID del registro de bloqueo
 * (accounting_shipment_blocks.id), no el shipment_id.
 * Muestra toasts de éxito/error y ejecuta callback de refetch al completar exitosamente.
 * 
 * @returns {Object} { unblockShipment: función de desbloqueo, isUnblocking: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';

export const useUnblockShipment = () => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { toast } = useToast();
  const [isUnblocking, setIsUnblocking] = useState(false);

  const unblockShipment = useCallback(
    async (
      blockId: number, // ID del registro de bloqueo (accounting_shipment_blocks.id)
      onSuccess?: () => void
    ) => {
      setIsUnblocking(true);

      try {
        const shipmentsAdapter = new ShipmentsApiAdapter(
          createHttpClient(apiClientWH)
        );
        await shipmentsAdapter.unblockShipment(blockId);

        // 🎯 Si llegamos aquí, la API devolvió 200 OK = éxito
        toast({
          title: t('toast.unblock_success.title', 'Shipment desbloqueado'),
          description: t('toast.unblock_success.description', 'El shipment se ha desbloqueado exitosamente'),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch de datos)
        onSuccess?.();
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;

        let description = apiMessage;

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.unblock_error.unexpected',
              'Error inesperado al desbloquear el shipment. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.unblock_error.title', 'Error al desbloquear'),
          description,
          variant: 'destructive',
        });
      } finally {
        setIsUnblocking(false);
      }
    },
    [t, toast]
  );

  return {
    unblockShipment,
    isUnblocking,
  };
};

